import React, { useEffect, useRef, useState } from 'react';
import { Form, Input, Button, Alert, InputRef, notification, Select } from 'antd';
import { clearError } from 'store/slices/authSlice';
import { AppDispatch } from 'store/store';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

interface AvigilonAltaLoginProps {
    onFinish: (values: { email: string; password: string; msiAltaApiHost:string, code?: string }) => void;
    onBack: () => void;
    error: string | null;
}

const AvigilonAltaLogin: React.FC<AvigilonAltaLoginProps> = ({ onBack, onFinish, error }) => {
    const dispatch: AppDispatch = useDispatch();

    const [showCodeInput, setShowCodeInput] = useState(false);

    const emailInputRef = useRef<InputRef>(null);

    const { t } = useTranslation();

    useEffect(() => {
        if (emailInputRef.current) {
            emailInputRef.current.focus();
        }
    }, []);

    useEffect(() => {
        if (error) {
            notification.error({
                message: t('auth.loginError'),
                description: t('auth.invalidCredentials'),
                placement: 'bottomRight',
            });
            dispatch(clearError());
        }
    }, [dispatch, error, t]);

    return (
        <Form
            layout="vertical"
            onFinish={onFinish}
            style={{ maxWidth: '400px', margin: '0 auto' }}
            initialValues={{ msiAltaApiHost: 'https://api.openpath.com' }}
        >
            <Alert
                message={t('auth.note')}
                type="info"
                showIcon
                style={{ marginBottom: '24px' }}
            />
            <Form.Item
                name="email"
                label={t('auth.email')}
                rules={[
                    { required: true, message: t('auth.emailRequired') },
                    { type: 'email', message: t('auth.emailInvalid') },
                ]}
            >
                <Input autoComplete="off" />
            </Form.Item>
            <Form.Item
                name="password"
                label={t('auth.password')}
                rules={[{ required: true, message: t('auth.passwordRequired') }]}
            >
                <Input.Password autoComplete="off" />
            </Form.Item>
            <Form.Item
                name="msiAltaApiHost"
                label={t('msiAltaApiHost')}
                rules={[{ required: true, message: t('msiAltaApiHostRequired') }]}
            >
                <Select
                    placeholder={t('msiAltaApiHostPlaceholder')}
                    options={[
                        { label: 'US', value: 'https://api.openpath.com' },
                        { label: 'Europe', value: 'https://api.eu.openpath.com' },
                    ]}
                />
            </Form.Item>

            {!showCodeInput && (
                <Button
                    type="link"
                    onClick={() => setShowCodeInput(true)}
                    style={{ padding: '0px', marginBottom: '8px' }}
                >
                    {t('auth.enterTwoFactorCode')}
                </Button>
            )}

            {showCodeInput && (
                <Form.Item
                    name="code"
                    label={t('auth.twoFactorPrompt')}
                    rules={[{ required: true, message: t('auth.twoFactorRequired') }]}
                >
                    <Input autoComplete="off" />
                </Form.Item>
            )}

            <Form.Item>
                <Button type="primary" htmlType="submit" block>
                    {t('auth.signIn')}
                </Button>
            </Form.Item>
            <Button
                onClick={onBack}
                type="link"
                className="button"
                style={{ fontWeight: 'bold' }}
            >
                {t('auth.returnToSignIn')}
            </Button>
        </Form>
    );
};

export default AvigilonAltaLogin;